import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle, ArrowRight, PackageX } from "lucide-react";
import { Button, Skeleton } from "@cieda/djayma-ui";
import { StockStatusBadge } from "@/components/admin/inventory/StockStatusBadge";
import { useInventoryStock } from "@/hooks/admin/useInventory";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface LowStockWidgetProps {
  readonly lowStockCount: number;
  readonly isLoading?: boolean;
}

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

function WidgetSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          className="flex items-center justify-between rounded-lg border border-stone-100 px-4 py-3"
        >
          <div className="space-y-1.5">
            <Skeleton className="h-3.5 w-28" />
            <Skeleton className="h-3 w-20" />
          </div>
          <Skeleton className="h-5 w-12 rounded-full" />
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function LowStockWidget({
  lowStockCount,
  isLoading: parentLoading,
}: LowStockWidgetProps) {
  const { t } = useTranslation(["admin", "dashboard"]);
  const navigate = useNavigate();

  // Fetch low-stock + out-of-stock items for the widget
  const { data, isLoading: stockLoading } = useInventoryStock({
    status: "low_stock",
    limit: 8,
    page: 1,
  });

  const isLoading = parentLoading || stockLoading;
  const products = data?.products ?? [];

  return (
    <div className="rounded-lg border border-stone-200 bg-white shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-stone-100 px-5 py-4">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-50 text-red-500">
            <AlertTriangle className="h-4 w-4" />
          </div>
          <div>
            <h3 className="font-display text-base font-semibold text-stone-900">
              {t("dashboard.low_stock_title", "Alertes de stock")}
            </h3>
            {lowStockCount > 0 && (
              <p className="text-xs text-red-600">
                {t("dashboard.low_stock_count", {
                  count: lowStockCount,
                  defaultValue: `${lowStockCount} produit(s) en stock faible`,
                })}
              </p>
            )}
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/admin/inventory")}
          className="gap-1.5 text-xs text-stone-500 hover:text-stone-700"
        >
          {t("dashboard.view_inventory", "Inventaire")}
          <ArrowRight className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Content */}
      <div className="px-5 py-4">
        {isLoading ? (
          <WidgetSkeleton />
        ) : products.length === 0 ? (
          <div className="flex flex-col items-center py-8 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-500">
              <PackageX className="h-5 w-5" />
            </div>
            <p className="mt-3 text-sm font-medium text-stone-600">
              {t(
                "dashboard.no_low_stock",
                "Tous les produits sont en stock.",
              )}
            </p>
            <p className="mt-1 text-xs text-stone-400">
              {t(
                "dashboard.no_low_stock_hint",
                "Aucune alerte de stock pour le moment.",
              )}
            </p>
          </div>
        ) : (
          <div className="space-y-1">
            {products.map((product) => (
              <div
                key={product.id}
                className="flex items-center justify-between rounded-lg px-3 py-2.5 transition-colors hover:bg-stone-50"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-stone-800">
                    {product.name}
                  </p>
                  <p className="mt-0.5 text-xs text-stone-500">
                    {product.brand} · {product.sku}
                  </p>
                </div>
                <div className="ml-4 flex shrink-0 items-center gap-3">
                  <div className="text-right">
                    <p className="text-sm font-semibold tabular-nums text-stone-900">
                      {product.current_stock}
                    </p>
                    <p className="text-[10px] text-stone-400">
                      {t("dashboard.stock_threshold", "seuil")}: {product.alert_threshold}
                    </p>
                  </div>
                  <StockStatusBadge status={product.status} />
                </div>
              </div>
            ))}

            {/* Show more indicator */}
            {lowStockCount > products.length && (
              <div className="pt-2 text-center">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/admin/inventory")}
                  className="text-xs text-amber-700 hover:text-amber-800"
                >
                  {t("dashboard.see_more_alerts", {
                    count: lowStockCount - products.length,
                    defaultValue: `+ ${lowStockCount - products.length} autre(s)`,
                  })}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
